import React from 'react';
import {View} from 'react-native';
import {isEmpty, isFunction, noop} from 'lodash';

import Avatar from '/component/base/Avatar';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {withTheme} from '/provider/ThemeProvider';

import {styles} from './PeopleExtendedData.style';

interface Person {
  accountEmail: string;
  avatarUrl: string;
  firstName: string;
  lastName: string;
}

interface PeopleExtendedDataProps {
  getTheme: (key: string) => any;
  style: {base: object};
  data: {
    target: any;
  };
  getPeople: (data: {target: any}) => Person[];
  personIsCoach: boolean;
  getVacantName: (params: {avatarNumber: number}) => string;
  getVacancyCta: () => string;
  getPersonDescription: (person: Person) => string;
  getPersonStats: (person: Person) => string | null;
  onVacancyCtaPress: () => void;
}

const PeopleExtendedData = ({
  getTheme = noop,
  style: styleProp,
  data = {
    target: 0,
  },
  getPeople = () => [],
  personIsCoach = false,
  getVacantName = () => '',
  getVacancyCta = () => '',
  getPersonDescription = () => '',
  getPersonStats = () => null,
  onVacancyCtaPress = noop,
}: PeopleExtendedDataProps) => {
  const {target} = data;
  const people = getPeople(data) || [];

  const style = [styles.base];
  if (styleProp?.base) {
    style.push(styleProp.base);
  }

  const renderedAvatars = [];
  let avatarNumber = 1;
  while (avatarNumber <= target) {
    const person = people[avatarNumber - 1];

    let avatarContent = (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        variant="caption"
        emphasis="low">
        {avatarNumber}
      </Text>
    );

    let nameContent = (
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        emphasis="low"
        style={styles.vacantName}>
        {getVacantName({avatarNumber})}
      </Text>
    );

    let descriptionContent = null;
    let statsContent = null;

    if (person) {
      const {accountEmail, avatarUrl, firstName, lastName} = person;

      avatarContent = (
        <Avatar
          size={40}
          avatarUrl={avatarUrl}
          avatarKey={accountEmail}
          isCoach={personIsCoach}
        />
      );

      nameContent = (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.personName}>
          {firstName} {lastName}
        </Text>
      );

      const description = getPersonDescription(person);
      descriptionContent = !isEmpty(description) ? (
        <Text allowFontScaling maxFontSizeMultiplier={2} emphasis="low">
          {description}
        </Text>
      ) : null;

      const stats = isFunction(getPersonStats) ? getPersonStats(person) : null;
      statsContent = !isEmpty(stats) ? (
        <View style={styles.personStats}>
          <Text allowFontScaling maxFontSizeMultiplier={2}>
            {stats}
          </Text>
        </View>
      ) : null;
    }

    const rowStyle = [styles.personRow];
    if (avatarNumber > 1) {
      rowStyle.push(styles.personRowWithBorder);
    }

    renderedAvatars.push(
      <View key={avatarNumber} style={rowStyle}>
        <View style={styles.avatarColumn}>
          <View style={person ? styles.filledAvatar : styles.emptyAvatar}>
            {avatarContent}
          </View>
        </View>
        <View style={styles.infoColumn}>
          {nameContent}
          {descriptionContent}
          {statsContent}
        </View>
      </View>,
    );

    avatarNumber += 1;
  }

  const seeMoreTextStyle = {
    ...getTheme('competency.textColor'),
  };

  let seeMoreCta = null;
  if (isFunction(getVacancyCta)) {
    const ctaText = getVacancyCta();
    if (!isEmpty(ctaText)) {
      seeMoreCta = (
        <View style={styles.seeMore}>
          <TouchableOpacity
            style={styles.seeMoreContent}
            onPress={onVacancyCtaPress}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={seeMoreTextStyle}>
              {ctaText}
            </Text>
          </TouchableOpacity>
        </View>
      );
    }
  }

  return (
    <View style={style}>
      {renderedAvatars}
      {seeMoreCta}
    </View>
  );
};

export default withTheme(PeopleExtendedData);
